<template>
  <div class="card">
    <div class="card-header is-flex is-justify-content-space-between is-align-items-center">
      <h2 class="card-header-title">Informations</h2>
      <b-tooltip :label="resyncTooltip" position="is-left">
        <b-button
          class="is-success mr-2"
          size="is-small"
          :label="
            resyncRemainingTime
              ? `Resynchroniser (${resyncRemainingTime}s)`
              : 'Resynchroniser par SMS'
          "
          type="is-danger"
          icon-right="sync"
          :disabled="disabledResync"
          :loading="loading"
          @click="resync"
        />
      </b-tooltip>
    </div>
    <div class="card-content">
      <o-table :items="informationRows" :loading="false" :draggable="false" :show-total="false">
        <b-table-column v-slot="{ row }" field="label">
          <span class="has-text-weight-bold">{{ row.label }}</span>
        </b-table-column>

        <b-table-column v-slot="{ row }" field="key">
          <o-table
            v-if="row.key === 'intervals'"
            :items="intervalModes"
            :loading="false"
            :draggable="false"
            :show-total="false"
          >
            <b-table-column v-slot="{ row }" field="title" label="Mode">
              <b-icon :icon="row.icon" size="is-small" type="is-primary" class="mr-1" />
              <span class="has-text-weight-bold">{{ row.title }}</span>
            </b-table-column>

            <b-table-column v-slot="{ row }" field="intervalSeconds" label="Intervalle" numeric>
              {{ formatDuration(row.intervalSeconds) }}
            </b-table-column>

            <b-table-column
              v-slot="{ row }"
              field="pointsPerMessage"
              label="Points par envoi"
              numeric
            >
              {{ row.pointsPerMessage || MIN_POINTS_PER_MESSAGE }}
            </b-table-column>

            <b-table-column v-slot="{ row }" field="sendIntervalSeconds" label="Envoi" numeric>
              <b-tag type="is-info is-light">{{ formatDuration(row.sendIntervalSeconds) }}</b-tag>
            </b-table-column>
          </o-table>

          <template v-else-if="row.key === 'sleep'">
            <b-tag v-if="queclinkTrackerConfig.sleepEnabled">ON</b-tag>
            <b-tag v-else type="is-danger">OFF</b-tag>
          </template>

          <template v-else>
            <b-icon v-if="row.icon" size="is-small" :type="row.iconType" :icon="row.icon" />
            {{ row.text }}
            <b-tooltip
              v-if="row.nextSynchronizedAt"
              label="Prochaine synchronisation (estimation)"
              position="is-top"
            >
              <b-icon size="is-small" type="is-info" icon="sync" />
              {{ row.nextSynchronizedAt }}
            </b-tooltip>
          </template>
        </b-table-column>
      </o-table>
    </div>
  </div>
</template>

<script>
import { QUECLINK_TRACKER_CONFIG_SYNC_STATUS } from '@constants/queclinkTrackerConfig/syncStatus'
import { mapActions } from 'vuex'
import { format } from 'date-fns'
import { formatDuration } from '@helpers/formatDuration'
import { MIN_POINTS_PER_MESSAGE } from '@constants/queclinkTrackerConfig/pointsPerMessage'
import OTable from '@components/Table.vue'

// Minimum time between each resync
const RESYNC_COUNTDOWN_IN_SECONDS = 180
const RESYNC_COUNTDOWN_INTERVAL_IN_SECONDS = 1
const QUECLINK_TRACKER_CONFIG_RESYNC_TIMESTAMP = 'queclink_tracker_config_resync_timestamp'

export default {
  name: 'QueclinkTrackerConfigInformation',

  components: {
    OTable,
  },

  props: {
    queclinkTracker: {
      type: Object,
      required: true,
    },
    queclinkTrackerConfig: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      resyncInterval: null,
      resyncRemainingTime: 0,
    }
  },

  async created() {
    if (this.queclinkTrackerConfig.isProcessing) {
      this.startResyncCountdownFromLocalStorage()
    }
  },

  beforeDestroy() {
    window.clearInterval(this.resyncInterval)
  },

  watch: {
    queclinkTrackerConfig: {
      handler(newConfig, oldConfig) {
        if (this.canResetResyncCountdown(newConfig, oldConfig)) {
          this.resetResyncCountdown()
        }
      },
      deep: true,
    },
  },

  computed: {
    MIN_POINTS_PER_MESSAGE: () => MIN_POINTS_PER_MESSAGE,

    informationRows() {
      const config = this.queclinkTrackerConfig

      return [
        {
          key: 'initialized',
          label: 'Initialisé',
          icon: config.isInitialized ? 'check' : 'times',
          iconType: config.isInitialized ? 'is-success' : 'is-danger',
          text: config.isInitialized ? this.format(config.initializedAt) : '',
        },
        { key: 'synchronized', label: 'Synchronisé', ...this.synchronizedRow },
        { key: 'status', label: 'Status', text: this.getStatus(config.syncStatus) },
        { key: 'intervals', label: 'Intervalles' },
        { key: 'sleep', label: 'Veille' },
      ]
    },

    synchronizedRow() {
      const config = this.queclinkTrackerConfig

      if (config.isProcessing) {
        return {
          icon: 'hourglass-half',
          iconType: 'is-info',
          text: this.format(config.synchronizedAt),
        }
      }

      if (!config.synchronizedAt) {
        return { icon: 'times', iconType: 'is-danger', text: '' }
      }

      return {
        icon: 'check',
        iconType: 'is-success',
        text: this.format(config.synchronizedAt),
        nextSynchronizedAt: config.expectedNextSynchronizedAt
          ? this.format(config.expectedNextSynchronizedAt)
          : null,
      }
    },

    intervalModes() {
      return [
        {
          key: 'idle',
          icon: 'moon',
          title: 'Repos',
          intervalSeconds: this.queclinkTrackerConfig.idleIntervalSeconds,
          pointsPerMessage: null,
          sendIntervalSeconds: this.queclinkTrackerConfig.idleIntervalSeconds,
        },
        {
          key: 'default',
          icon: 'location-arrow',
          title: 'Défaut',
          intervalSeconds: this.queclinkTrackerConfig.defaultIntervalSeconds,
          pointsPerMessage: this.queclinkTrackerConfig.defaultPointsPerMessage,
          sendIntervalSeconds: this.queclinkTrackerConfig.defaultSendIntervalSeconds,
        },
        {
          key: 'performance',
          icon: 'tachometer-alt',
          title: 'Performance',
          intervalSeconds: this.queclinkTrackerConfig.performanceIntervalSeconds,
          pointsPerMessage: this.queclinkTrackerConfig.performancePointsPerMessage,
          sendIntervalSeconds: this.queclinkTrackerConfig.performanceSendIntervalSeconds,
        },
      ]
    },

    disabledResync() {
      return !this.queclinkTracker.emnifyEndpointId || this.resyncRemainingTime > 0
    },

    resyncTooltip() {
      if (!this.queclinkTracker.emnifyEndpointId) {
        return 'Aucun endpoint Emnify associé'
      }

      return this.resyncRemainingTime
        ? 'Synchronisation en cours...'
        : 'Envoyer un SMS pour forcer la configuration'
    },
  },

  methods: {
    ...mapActions('ui', ['addToastMessage']),

    async resync() {
      this.loading = true

      try {
        const queclinkTrackerConfig = await this.$services.queclinkTrackerConfigService.resync(
          this.queclinkTracker.id,
        )

        this.startResyncCountdown(RESYNC_COUNTDOWN_IN_SECONDS)

        this.$emit('update:queclinkTrackerConfig', queclinkTrackerConfig)

        this.addToastMessage({
          text: 'Resynchronisation en cours...',
          type: 'is-info',
        })
      } catch (err) {
        this.addToastMessage({
          text: err?.message || 'Une erreur interne est survenue.',
          type: 'is-danger',
        })
        console.error(err)
      }

      this.loading = false
    },

    getStatus(status) {
      const syncStatus = QUECLINK_TRACKER_CONFIG_SYNC_STATUS[status]

      return syncStatus ?? 'Aucune configuration'
    },

    format(date) {
      return format(date, 'dd/MM/yyyy HH:mm')
    },

    formatDuration,

    startResyncCountdown(remainingTime) {
      localStorage.setItem(
        QUECLINK_TRACKER_CONFIG_RESYNC_TIMESTAMP,
        new Date().getTime() + remainingTime * 1000,
      )

      this.resyncRemainingTime = remainingTime

      if (this.resyncInterval) {
        window.clearInterval(this.resyncInterval)
      }

      this.resyncInterval = window.setInterval(() => {
        if (this.resyncRemainingTime <= 0) {
          window.clearInterval(this.resyncInterval)
          return
        }

        this.resyncRemainingTime--
      }, RESYNC_COUNTDOWN_INTERVAL_IN_SECONDS * 1000)
    },

    resetResyncCountdown() {
      this.resyncRemainingTime = 0
      window.clearInterval(this.resyncInterval)
      localStorage.removeItem(QUECLINK_TRACKER_CONFIG_RESYNC_TIMESTAMP)
    },

    startResyncCountdownFromLocalStorage() {
      const timestamp = Number(localStorage.getItem(QUECLINK_TRACKER_CONFIG_RESYNC_TIMESTAMP))
      if (!timestamp) {
        return
      }

      const remainingTime = Math.floor((timestamp - new Date().getTime()) / 1000)
      if (remainingTime <= 0) {
        return
      }

      this.startResyncCountdown(remainingTime)
    },

    canResetResyncCountdown(newConfig, oldConfig) {
      if (newConfig.isSynchronized) {
        return true
      }

      if (newConfig.performanceIntervalSeconds !== oldConfig.performanceIntervalSeconds) {
        return true
      }

      if (newConfig.performancePointsPerMessage !== oldConfig.performancePointsPerMessage) {
        return true
      }

      if (newConfig.defaultIntervalSeconds !== oldConfig.defaultIntervalSeconds) {
        return true
      }

      if (newConfig.defaultPointsPerMessage !== oldConfig.defaultPointsPerMessage) {
        return true
      }

      if (newConfig.idleIntervalSeconds !== oldConfig.idleIntervalSeconds) {
        return true
      }

      return newConfig.sleepEnabled !== oldConfig.sleepEnabled
    },
  },
}
</script>
